import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from "react";
import axios from "axios";

const AuthContext = createContext();

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://swadvedamasalasbe.onrender.com/api";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // =========================
  // FETCH PROFILE
  // =========================
  const fetchProfile = useCallback(async (authToken) => {
    try {
      const res = await axios.get(`${API_URL}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      setUser(res.data);
      setIsAuthenticated(true);
    } catch (err) {
      console.error("Profile fetch failed:", err);

      // invalid token → logout
      localStorage.removeItem("token");
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
    }
  }, []);

  // =========================
  // INIT (APP LOAD)
  // =========================
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem("token");

        if (!savedToken) {
          setLoading(false);
          return;
        }

        setToken(savedToken);
        await fetchProfile(savedToken); // ✅ wait for user

      } catch (err) {
        console.error("Auth init failed:", err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [fetchProfile]);

  // =========================
  // LOGIN
  // =========================
  const login = useCallback(async (authToken) => {
    localStorage.setItem("token", authToken);
    setToken(authToken);

    // 🔥 VERY IMPORTANT → wait for profile
    await fetchProfile(authToken);

  }, [fetchProfile]);

  // =========================
  // LOGOUT
  // =========================
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        login,
        logout,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// =========================
// HOOK
// =========================
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};